import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faLock, faKey } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-modal';
import useIsMobile from 'common/hooks/useIsMobile';
import DeviceChecklistService from 'api/deviceChecklist/DeviceChecklistService';

interface FotaLoginModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

type Step = 'credentials' | 'tfa';

const Spinner: React.FC = () => (
    <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
);

const FotaLoginModal: React.FC<FotaLoginModalProps> = ({ open, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [step, setStep] = useState<Step>('credentials');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [twoFactorAuthType, setTwoFactorAuthType] = useState<string>('Authenticator');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const reset = useCallback(() => {
        setStep('credentials');
        setEmail('');
        setPassword('');
        setOtpCode('');
        setTwoFactorAuthType('Authenticator');
        setError(null);
        setSubmitting(false);
    }, []);

    useEffect(() => {
        if (!open) reset();
    }, [open, reset]);

    const handleCancel = () => {
        if (submitting) return;
        onClose();
    };

    const handleSubmitCredentials = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting || !email.trim() || !password) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await DeviceChecklistService.fotaLogin(email.trim(), password);
            if (result.authenticated) {
                onSuccess();
                return;
            }
            if (result.twoFactorRequired) {
                setTwoFactorAuthType(result.twoFactorAuthType || 'Authenticator');
                setStep('tfa');
                setError(null);
                return;
            }
            setError(result.error || t('deviceChecklist.fota_login_generic_error'));
        } catch {
            setError(t('deviceChecklist.fota_login_generic_error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting || !otpCode.trim()) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await DeviceChecklistService.fotaLoginTfa(otpCode.trim(), twoFactorAuthType);
            if (result.authenticated) {
                onSuccess();
                return;
            }
            setError(result.error || t('deviceChecklist.fota_login_tfa_invalid_code'));
        } catch {
            setError(t('deviceChecklist.fota_login_generic_error'));
        } finally {
            setSubmitting(false);
        }
    };

    const tfaHintKey = twoFactorAuthType === 'Email'
        ? 'deviceChecklist.fota_login_tfa_hint_email'
        : 'deviceChecklist.fota_login_tfa_hint_authenticator';

    const contentStyle: React.CSSProperties = {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '92%' : '400px',
        maxWidth: '92%',
        padding: 0,
        backgroundClip: 'padding-box',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '1rem',
        boxShadow: '0 16px 48px rgba(11, 17, 33, 0.16)',
        outline: '0 none',
        overflow: 'hidden',
    };
    const overlayStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        backgroundColor: 'rgba(11, 17, 33, 0.4)',
        zIndex: 1001,
        backdropFilter: 'blur(2px)',
    };

    return (
        <Modal isOpen={open} style={{ content: contentStyle, overlay: overlayStyle }}>
            <div>
                {/* Header */}
                <div className="bg-slate-900 px-4 py-4 flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0 text-white">
                        <FontAwesomeIcon icon={step === 'credentials' ? faLock : faKey} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm leading-tight m-0 truncate">
                            {step === 'credentials'
                                ? t('deviceChecklist.fota_login_title')
                                : t('deviceChecklist.fota_login_tfa_title')}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5 m-0">Teltonika FOTA Web</p>
                    </div>
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={submitting}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors disabled:opacity-50 border-0 bg-transparent shrink-0"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                {step === 'credentials' && (
                    <form onSubmit={handleSubmitCredentials} className="p-4 space-y-3">
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                                {t('deviceChecklist.fota_login_email')}
                            </label>
                            <input
                                type="email"
                                autoFocus
                                autoComplete="off"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                disabled={submitting}
                                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 disabled:opacity-60"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                                {t('deviceChecklist.fota_login_password')}
                            </label>
                            <input
                                type="password"
                                autoComplete="off"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                disabled={submitting}
                                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 disabled:opacity-60"
                            />
                        </div>
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-3 py-2">{error}</div>
                        )}
                        <div className="flex gap-2 pt-1">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={submitting}
                                className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 active:scale-95 transition-all disabled:opacity-50 bg-white"
                            >
                                {t('deviceChecklist.fota_login_cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={submitting || !email.trim() || !password}
                                className="flex-1 flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 active:scale-95 text-white text-sm font-semibold rounded-xl px-4 py-2.5 border-0 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? <Spinner /> : t('deviceChecklist.fota_login_submit')}
                            </button>
                        </div>
                    </form>
                )}

                {step === 'tfa' && (
                    <form onSubmit={handleSubmitOtp} className="p-4 space-y-3">
                        <p className="text-xs text-slate-500 leading-relaxed m-0">{t(tfaHintKey)}</p>
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                                {t('deviceChecklist.fota_login_tfa_code')}
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                autoComplete="one-time-code"
                                maxLength={8}
                                value={otpCode}
                                onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                                disabled={submitting}
                                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-base font-mono tracking-widest text-center bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 disabled:opacity-60"
                            />
                        </div>
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-3 py-2">{error}</div>
                        )}
                        <div className="flex gap-2 pt-1">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={submitting}
                                className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 active:scale-95 transition-all disabled:opacity-50 bg-white"
                            >
                                {t('deviceChecklist.fota_login_cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={submitting || !otpCode.trim()}
                                className="flex-1 flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 active:scale-95 text-white text-sm font-semibold rounded-xl px-4 py-2.5 border-0 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? <Spinner /> : t('deviceChecklist.fota_login_submit')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Modal>
    );
};

export default FotaLoginModal;
